import { Box, Divider, Grid, Paper, Typography } from '@mui/material';
import { format } from 'date-fns';
import { isEmpty, round, sortBy } from 'lodash';
import { PureComponent, useCallback } from 'react';
import { isMobile } from 'react-device-detect';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ReferenceLine,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import MetricsRangeSelectorComponent from './MetricsRangeSelectorComponent';

// Design System Constants
const SPACING = {
  XS: 1,
  SM: 2,
  MD: 3,
  LG: 4,
};

const DESIGN_COLORS = {
  BACKGROUND: '#f8f9fa',
  SURFACE: '#ffffff',
  BORDER: '#e8eaed',
  TEXT_SECONDARY: '#5f6368',
  TEXT_PRIMARY: '#202124',
  ACCENT_PRIMARY: '#1f2937',
  ACCENT_SECONDARY: '#6366f1',
};

const CHART_COLORS = {
  VALUE: '#FFBB28',
  CONTRIBUTIONS: '#00C49F',
  PORTFOLIO: '#FFBB28',
  NASDAQ: '#00C49F',
  SP500: '#0088FE',
  GRID: '#ccc',
  REFERENCE: '#blue',
  DIVIDENDS: '#00C49F',
  DIVIDENDS_YTD: '#9BE3D2',
};

const eurFormatter = new Intl.NumberFormat('es-ES', {
  style: 'currency',
  currency: 'EUR',
});

const PIE_CHART_COLORS = [
  '#FF6B6B',
  '#4ECDC4',
  '#45B7D1',
  '#FFA07A',
  '#98D8C8',
  '#F7DC6F',
  '#BB8FCE',
  '#85C1E2',
  '#F8B88B',
  '#ABEBC6',
  '#F1948A',
  '#52D3AA',
  '#5DADE2',
  '#F8C471',
  '#D7BDE2',
  '#A9DFBF',
];

const CHART_DIMENSIONS = {
  MOBILE_HEIGHT_LARGE: 350,
  MOBILE_HEIGHT_SMALL: 250,
  MOBILE_HEIGHT_COMPOSITION: 150,
  MOBILE_WIDTH: 734,
  DESKTOP_HEIGHT_LARGE: 300,
  DESKTOP_HEIGHT_SMALL: 300,
  DESKTOP_HEIGHT_COMPOSITION: 250,
  DESKTOP_WIDTH: 500,
  COMPOSITION_ASPECT: 4 / 3,
};

const THRESHOLDS = {
  POSITION_WEIGHT_CUTOFF: 2,
};

const AXIS_CONFIG = {
  TEXT_SIZE: 12,
  ROTATION: -60,
  GRID_DASH: '5 5',
  COMPOSITION_DASH: '3 3',
  STROKE_WIDTH: 2,
};

class CustomizedAxisTick extends PureComponent {
  render() {
    const { x, y, payload } = this.props;

    return (
      <g transform={`translate(${x},${y})`}>
        <text
          x={0}
          y={0}
          dy={16}
          textAnchor="end"
          fill="#666"
          transform="rotate(-60)"
          fontSize={12}
        >
          {payload.value}
        </text>
      </g>
    );
  }
}

export default function PortfolioAnalyticsComponent(props) {
  const renderAverageBalancesLineChart = useCallback(() => {
    const data = sortBy(props?.portfolioMetrics?.averageBalances, [
      'timestamp',
    ]).map((averageBalance) => ({
      formattedTs: format(new Date(averageBalance.timestamp), 'dd/MM/yy'),
      average: Math.round(averageBalance.average * 100) / 100,
      contributions: averageBalance.contributions,
    }));

    return (
      <LineChart
        data={data}
        height={
          isMobile
            ? CHART_DIMENSIONS.MOBILE_HEIGHT_LARGE
            : CHART_DIMENSIONS.DESKTOP_HEIGHT_LARGE
        }
        width={
          isMobile
            ? CHART_DIMENSIONS.MOBILE_WIDTH
            : CHART_DIMENSIONS.DESKTOP_WIDTH
        }
      >
        <Line
          type="monotone"
          dataKey="average"
          name="Value (EUR)"
          stroke={CHART_COLORS.VALUE}
        />
        <Line
          type="monotone"
          dataKey="contributions"
          name="Accumulated contributions"
          stroke={CHART_COLORS.CONTRIBUTIONS}
        />
        <CartesianGrid
          stroke={CHART_COLORS.GRID}
          strokeDasharray={AXIS_CONFIG.GRID_DASH}
        />
        <YAxis
          domain={[
            (dataMin) => Math.round(dataMin),
            (dataMax) => Math.round(dataMax),
          ]}
        />
        <XAxis
          dataKey="formattedTs"
          height={20}
          tick={<CustomizedAxisTick />}
        />
        {props?.contributions.items
          .map((i) => format(new Date(i.timestamp), 'dd/MM/yy'))
          .map((i, idx) => (
            <ReferenceLine key={idx} x={i} stroke={CHART_COLORS.REFERENCE} />
          ))}
        <Tooltip />
        <Legend />
      </LineChart>
    );
  }, [props?.portfolioMetrics?.averageBalances, props?.contributions.items]);

  const renderReturnsLineChart = useCallback(() => {
    const data = sortBy(props?.portfolioMetrics?.returns, ['timestamp']).map(
      (dataPoint) => ({
        formattedTs: format(new Date(dataPoint.timestamp), 'dd/MM/yy'),
        portfolio: round(Number(dataPoint.value), 2),
        sp: round(Number(dataPoint['S&P 500']), 2),
        nasdaq: round(Number(dataPoint['NASDAQ Composite']), 2),
      }),
    );

    if (!isEmpty(data)) {
      return (
        <LineChart
          data={data}
          height={
            isMobile
              ? CHART_DIMENSIONS.MOBILE_HEIGHT_SMALL
              : CHART_DIMENSIONS.DESKTOP_HEIGHT_SMALL
          }
          width={
            isMobile
              ? CHART_DIMENSIONS.MOBILE_WIDTH
              : CHART_DIMENSIONS.DESKTOP_WIDTH
          }
        >
          <ReferenceLine y={0} stroke="green" strokeDasharray="3 3" />
          <Line
            type="monotone"
            dataKey="portfolio"
            stroke={CHART_COLORS.PORTFOLIO}
            name="Portfolio"
            legendType="line"
          />
          <Line
            type="monotone"
            dataKey="nasdaq"
            stroke={CHART_COLORS.NASDAQ}
            name="NASDAQ"
            legendType="line"
          />
          <Line
            type="monotone"
            dataKey="sp"
            stroke={CHART_COLORS.SP500}
            name="S&P 500"
            legendType="line"
          />
          <CartesianGrid
            stroke={CHART_COLORS.GRID}
            strokeDasharray={AXIS_CONFIG.GRID_DASH}
          />
          <YAxis
            domain={[
              (dataMin) => Math.round(dataMin),
              (dataMax) => Math.round(dataMax),
            ]}
            label={{
              value: 'ROI %',
              angle: -90,
              position: 'insideStart',
            }}
          />
          <XAxis
            dataKey="formattedTs"
            height={60}
            tick={<CustomizedAxisTick />}
          />
          <Tooltip />
          <Legend />
        </LineChart>
      );
    }
  }, [props?.portfolioMetrics?.returns]);

  const renderCompositionChart = useCallback(() => {
    const positions = sortBy(
      props?.portfolio?.positions.map((p) => ({
        ...p,
        currentWeight: Number(p.currentWeight),
      })),
      'currentWeight',
    ).reverse();
    const mainPositions = positions.filter(
      (p) => p.currentWeight >= THRESHOLDS.POSITION_WEIGHT_CUTOFF,
    );
    const minorPositions = positions.filter(
      (p) => p.currentWeight < THRESHOLDS.POSITION_WEIGHT_CUTOFF,
    );

    const data = [
      ...mainPositions,
      ...(minorPositions.length > 0
        ? [
            {
              companyName: 'Other',
              currentWeight: minorPositions.reduce(
                (acc, position) => acc + position.currentWeight,
                0,
              ),
            },
          ]
        : []),
    ];

    return (
      <PieChart
        height={
          isMobile
            ? CHART_DIMENSIONS.MOBILE_HEIGHT_COMPOSITION + 100
            : CHART_DIMENSIONS.DESKTOP_HEIGHT_COMPOSITION + 50
        }
        width={
          isMobile
            ? CHART_DIMENSIONS.MOBILE_WIDTH
            : CHART_DIMENSIONS.DESKTOP_WIDTH + 100
        }
      >
        <Pie
          data={data}
          dataKey="currentWeight"
          nameKey="symbol"
          cx="50%"
          cy="50%"
          outerRadius={isMobile ? 80 : 100}
          label={(entry) =>
            `${entry.symbol} (${round(entry.currentWeight, 1)}%)`
          }
          labelLine={false}
        >
          {data.map((entry, index) => (
            <Cell
              key={`cell-${index}`}
              fill={PIE_CHART_COLORS[index % PIE_CHART_COLORS.length]}
            />
          ))}
        </Pie>
        <Tooltip
          formatter={(value) => `${round(value, 2)}%`}
          contentStyle={{
            backgroundColor: '#fff',
            border: `1px solid ${DESIGN_COLORS.BORDER}`,
            borderRadius: '8px',
          }}
        />
      </PieChart>
    );
  }, [props?.portfolio?.positions]);

  const renderTopPerformersBarChart = useCallback(() => {
    const chartData = props?.portfolio?.positions
      ?.map((p) => ({
        name: p.symbol.slice(0, 4),
        pnl: parseFloat(p.positionState?.pnlAmount || 0),
      }))
      .sort((a, b) => b.pnl - a.pnl)
      .slice(0, 10);

    if (!chartData || chartData.length === 0) return null;

    return (
      <BarChart
        data={chartData}
        height={
          isMobile
            ? CHART_DIMENSIONS.MOBILE_HEIGHT_LARGE
            : CHART_DIMENSIONS.DESKTOP_HEIGHT_LARGE
        }
        width={
          isMobile
            ? CHART_DIMENSIONS.MOBILE_WIDTH
            : CHART_DIMENSIONS.DESKTOP_WIDTH
        }
        margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
      >
        <CartesianGrid
          stroke={CHART_COLORS.GRID}
          strokeDasharray={AXIS_CONFIG.GRID_DASH}
          vertical={false}
        />
        <XAxis dataKey="name" fontSize={11} tickLine={false} axisLine={false} />
        <YAxis
          fontSize={12}
          tickLine={false}
          axisLine={false}
          tickFormatter={(value) => `$${value}`}
        />
        <Tooltip
          cursor={{ fill: 'transparent' }}
          formatter={(value) => [`$${value}`, 'PnL']}
        />
        <Bar dataKey="pnl" radius={[4, 4, 0, 0]}>
          {chartData.map((_, index) => (
            <Cell
              key={`cell-${index}`}
              fill={PIE_CHART_COLORS[index % PIE_CHART_COLORS.length]}
            />
          ))}
        </Bar>
      </BarChart>
    );
  }, [props?.portfolio]);

  const renderDividendIncome = useCallback(() => {
    const projectedTotal = Number(
      props?.dividends?.projection?.netAmountEURTotal || 0,
    );
    const stocksTotal = Number(props?.stocksTotalEUR || 0);
    const currentYear = new Date().getFullYear();
    // The API returns years newest first; the chart reads left to right.
    const yearlyData = [...(props?.dividends?.yearly || [])]
      .reverse()
      .map((y) => ({
        year: String(y.year),
        net: round(Number(y.netAmountEUR), 2),
        count: y.count,
        isCurrent: y.year === currentYear,
      }));

    if (!projectedTotal && isEmpty(yearlyData)) {
      return (
        <Typography variant="body2" color={DESIGN_COLORS.TEXT_SECONDARY}>
          No dividend data yet
        </Typography>
      );
    }

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
        <Box sx={{ textAlign: 'center', mb: SPACING.SM }}>
          <Typography
            variant="h4"
            color={DESIGN_COLORS.TEXT_PRIMARY}
            sx={{ fontWeight: 700 }}
          >
            {eurFormatter.format(projectedTotal)}
          </Typography>
          <Typography variant="body2" color={DESIGN_COLORS.TEXT_SECONDARY}>
            Expected net income, next 12 months
            {stocksTotal > 0 &&
              ` · ${round((projectedTotal / stocksTotal) * 100, 2)}% yield`}
          </Typography>
        </Box>
        {!isEmpty(yearlyData) && (
          <>
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              <BarChart
                data={yearlyData}
                height={
                  isMobile
                    ? CHART_DIMENSIONS.MOBILE_HEIGHT_SMALL
                    : CHART_DIMENSIONS.DESKTOP_HEIGHT_SMALL - 80
                }
                width={
                  isMobile
                    ? CHART_DIMENSIONS.MOBILE_WIDTH
                    : CHART_DIMENSIONS.DESKTOP_WIDTH
                }
                margin={{ top: 10, right: 30, left: 20, bottom: 5 }}
              >
                <CartesianGrid
                  stroke={CHART_COLORS.GRID}
                  strokeDasharray={AXIS_CONFIG.GRID_DASH}
                  vertical={false}
                />
                <XAxis
                  dataKey="year"
                  fontSize={12}
                  tickLine={false}
                  axisLine={false}
                />
                <YAxis
                  fontSize={12}
                  tickLine={false}
                  axisLine={false}
                  tickFormatter={(value) => `${value}€`}
                />
                <Tooltip
                  cursor={{ fill: 'transparent' }}
                  formatter={(value, name, { payload }) => [
                    `${eurFormatter.format(value)} (${payload.count} payments)`,
                    payload.isCurrent ? 'Received so far' : 'Received',
                  ]}
                />
                <Bar dataKey="net" radius={[4, 4, 0, 0]}>
                  {yearlyData.map((entry) => (
                    <Cell
                      key={entry.year}
                      fill={
                        entry.isCurrent
                          ? CHART_COLORS.DIVIDENDS_YTD
                          : CHART_COLORS.DIVIDENDS
                      }
                    />
                  ))}
                </Bar>
              </BarChart>
            </Box>
            <Typography
              variant="caption"
              color={DESIGN_COLORS.TEXT_SECONDARY}
              sx={{ textAlign: 'center' }}
            >
              Net dividends received per year. {currentYear} is year to date.
            </Typography>
          </>
        )}
      </Box>
    );
  }, [props?.dividends, props?.stocksTotalEUR]);

  const ChartWrapper = ({ title, children, gridSize = 4 }) => (
    <Grid item xs={12} sm={12} md={gridSize}>
      <Paper
        elevation={0}
        sx={{
          p: SPACING.MD,
          borderRadius: '12px',
          border: `1px solid ${DESIGN_COLORS.BORDER}`,
          backgroundColor: DESIGN_COLORS.SURFACE,
          transition: 'all 0.3s ease',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.08)',
          '&:hover': {
            boxShadow: '0 8px 16px rgba(0, 0, 0, 0.1)',
          },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: SPACING.SM,
            mb: SPACING.MD,
          }}
        >
          <Box
            sx={{
              width: '4px',
              height: '24px',
              borderRadius: '2px',
              background: `linear-gradient(135deg, ${DESIGN_COLORS.ACCENT_SECONDARY} 0%, ${DESIGN_COLORS.ACCENT_PRIMARY} 100%)`,
            }}
          />
          <Typography
            variant="h6"
            color={DESIGN_COLORS.TEXT_PRIMARY}
            sx={{ fontWeight: 700, fontSize: '1rem' }}
          >
            {title}
          </Typography>
        </Box>
        <Divider sx={{ mb: SPACING.MD, borderColor: DESIGN_COLORS.BORDER }} />
        <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
          {children}
        </Box>
      </Paper>
    </Grid>
  );

  return (
    <Box
      sx={{
        p: SPACING.LG,
        px: isMobile ? 0 : SPACING.LG,
        backgroundColor: DESIGN_COLORS.BACKGROUND,
        minHeight: '100vh',
      }}
    >
      <Grid
        container
        rowSpacing={SPACING.LG}
        columnSpacing={isMobile ? 0 : SPACING.LG}
      >
        <ChartWrapper title="Valuation over time" gridSize={5}>
          {renderAverageBalancesLineChart()}
        </ChartWrapper>

        <ChartWrapper title="ROI vs. Indices" gridSize={5}>
          {renderReturnsLineChart()}
        </ChartWrapper>

        <Grid item xs={12} sm={12} md={2}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'flex-end',
              height: '100%',
              pt: SPACING.MD,
            }}
          >
            <MetricsRangeSelectorComponent
              metricsRange={props?.metricsRange}
              setMetricsRange={props?.setMetricsRange}
            />
          </Box>
        </Grid>

        <ChartWrapper title="Current portfolio composition" gridSize={5}>
          {renderCompositionChart()}
        </ChartWrapper>

        <ChartWrapper title="All-time top performers" gridSize={5}>
          {renderTopPerformersBarChart()}
        </ChartWrapper>

        <ChartWrapper title="Dividend income" gridSize={5}>
          {renderDividendIncome()}
        </ChartWrapper>
      </Grid>
    </Box>
  );
}
